import React, { useEffect, useMemo, useState } from 'react';
import { LinkOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { Tooltip, Tabs } from 'antd';

// helpers
import { copyText } from 'helper/text';

// hooks
import useToggle from '../../../../../../hooks/useToggle';

// components
import Handler from '../../edge/Handler';

// import Duplicate
import Duplicate from '../../../../../../assets/customSVG/Duplicate';

export default function ApiTriggerPreview({
  node,
  color,
  isConnected,
  onEdgeMouseDown,
  allowToEditFlow,
}) {
  const [isCopied, setIsCopied] = useState(false);
  const [isUrlCopied, setIsUrlCopied] = useToggle(false);

  useEffect(() => {
    if (isCopied || isUrlCopied) {
      const timeout = setTimeout(() => {
        if (isCopied) setIsCopied(false);
        if (isUrlCopied) setIsUrlCopied(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isCopied, isUrlCopied]);

  const connection_point = {
    id: node?.data?.id,
    type: 'action',
  };

  const webhookUrl = node?.data?.webhook_url || '';
  const displayUrl = webhookUrl.length > 30 
    ? `${webhookUrl.substring(0, 15)}...${webhookUrl.substring(webhookUrl.length - 15)}`
    : webhookUrl;
    
  // Generate payload preview
  const payloadPreview = useMemo(() => {
    const basePayload = {
      // mobile_number: "+1234567890",
      // sequence_id: sequence_id
    };
    
    // Add mapped fields
    if (node?.data?.attribute_mapping) {
      Object.entries(node.data.attribute_mapping).forEach(([apiParam]) => {
        basePayload[apiParam] = "value";
      });
    }
    
    return basePayload;
  }, [node?.data?.attribute_mapping]);

  const handleCopy = (text, type = 'text') => {
    if (!text) return;
    copyText(text);
    if (type === 'url') {
      setIsUrlCopied(true);
    } else {
      setIsCopied(true);
    }
    // message.success(`${type === 'url' ? 'URL' : 'Text'} copied to clipboard!`);
  };

  return (
    <div className="relative w-full h-full px-3 py-4 bg-white rounded-xl">
      <div className="relative w-full flex flex-col gap-4 p-3 bg-[#F8F9FC] rounded-[10px]">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-[500] text-gray-600">Webhook URL</span>
            <Tooltip title={isUrlCopied ? 'Copied!' : 'Copy URL'}>
              <button
                onClick={() => handleCopy(webhookUrl, 'url')}
                className="p-2 text-gray-500 hover:text-primary transition-colors duration-200 rounded"
                disabled={!webhookUrl}
                aria-label="Copy to clipboard"
                type="button"
              >
                <div className="relative w-5 h-5">
                  <Duplicate
                    width={18}
                    height={18}
                    className={`absolute inset-0 m-auto transition-all duration-200 ${
                      isUrlCopied ? 'opacity-0 scale-90' : 'opacity-100'
                    }`}
                  />
                  <div
                    className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${
                      isUrlCopied
                        ? 'opacity-100 text-green-500'
                        : 'opacity-0 -translate-y-1'
                    }`}
                  >
                    <CheckOutlined className="h-4 w-4" />
                  </div>
                </div>
              </button>
            </Tooltip>
          </div>
          <div className="flex items-center gap-2 p-2 bg-white rounded-md border border-gray-200">
            <LinkOutlined className="text-gray-400" />
            <span className="text-[13px] text-gray-600 truncate">
              {webhookUrl ? displayUrl : 'No webhook URL configured'}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-[13px] font-[500] text-gray-600">Method</span>
          <div className="px-3 py-1.5 bg-white rounded-md border border-gray-200">
            <span className="text-[13px] text-gray-600 truncate">
              {node?.data?.method || 'POST'}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-[500] text-gray-600">Mapped Fields</span>
            <span className="text-[13px] text-gray-500">
              {Object.keys(node?.data?.attribute_mapping || {}).length} fields
            </span>
          </div>
          <div className="max-h-24 overflow-y-auto">
            {Object.entries(node?.data?.attribute_mapping || {}).map(([apiParam, crmField]) => (
              <div key={apiParam} className="py-1 px-2 text-[13px] flex items-center">
                <span className="text-gray-700 font-[500]">{crmField}</span>
                <span className="mx-2 text-gray-400">→</span>
                <code className="text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{apiParam}</code>
              </div>
            ))}
            {Object.keys(node?.data?.attribute_mapping || {}).length === 0 && (
              <div className="text-[13px] text-gray-400 text-center py-2">
                No fields mapped yet
              </div>
            )}
          </div>
        </div>

        {/* Request Payload */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <h3 className="text-[13px] font-[500] text-gray-600">Request Payload</h3>
            <button
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(payloadPreview, null, 2));
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 2000);
              }}
              className="p-1 -mt-1 -mr-1 text-gray-500 hover:text-gray-700 transition-colors duration-200 rounded"
              aria-label="Copy payload to clipboard"
              type="button"
            >
              <div className="relative w-5 h-5">
                <Duplicate
                  className={`absolute inset-0 m-auto transition-all duration-200 ${
                    isCopied ? 'opacity-0 scale-90' : 'opacity-100'
                  }`}
                />
                <div
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${
                    isCopied
                      ? 'opacity-100 text-green-500'
                      : 'opacity-0 -translate-y-1'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </button>
          </div>
          <div className="bg-white p-3 rounded border border-gray-200 max-h-32 overflow-y-auto relative">
            <pre className="text-[13px] text-gray-800">
              {JSON.stringify(payloadPreview, null, 2)}
            </pre>
          </div>
        </div>

        <div className="absolute top-1/2 right-[-6px]">
          <Handler
            type="start"
            start_node_id={node?.uniq_id}
            isConnected={isConnected}
            onEdgeMouseDown={({ position, payload }) => {
              if (onEdgeMouseDown) {
                if (!isConnected)
                  onEdgeMouseDown({
                    connection_point,
                    position: position,
                    payload,
                  });
              }
            }}
            color={color}
            allowToEditFlow={allowToEditFlow}
          />
        </div>
      </div>
    </div>
  );
}
